/**
 * Prepara l'immagine di copertina prima dell'invio.
 *
 * Le copertine partivano cosi' come le sceglieva la redazione: al 29/09/2026 il
 * 60-80% erano PNG opache da 1672x941 e 2-4 MB, uscite da un generatore. In
 * base64 diventano 3-5 MB per ogni upload, e su una linea lenta sono i secondi
 * che si aspettano. La stessa immagine in JPEG pesa circa un dodicesimo.
 *
 * - lato lungo oltre LATO_MASSIMO: ridotto. Il CDN ne serve al massimo 1200 px
 *   e Google Discover ne chiede almeno 1200, quindi 2000 lascia margine;
 * - PNG senza trasparenza: convertita in JPEG;
 * - PNG con trasparenza: resta PNG, ridotta solo se serve;
 * - GIF: intatta, potrebbe essere animata;
 * - un file gia' leggero e nei limiti arriva intatto, e se la conversione non
 *   fa risparmiare si tiene l'originale.
 */

export interface CopertinaPronta {
  dataUrl: string;
  nome: string;
  tipo: string;
  byte: number;
}

const LATO_MASSIMO = 2000;
const QUALITA_JPEG = 0.86;
/** Sotto questa soglia un JPEG o un WebP nei limiti non vale la ricodifica. */
const BYTE_TRASCURABILI = 600 * 1024;

export async function preparaCopertina(file: File): Promise<CopertinaPronta> {
  const originale = async (): Promise<CopertinaPronta> => ({
    dataUrl: await leggi(file),
    nome: file.name,
    tipo: file.type,
    byte: file.size,
  });

  if (file.type === 'image/gif') return originale();

  let img: HTMLImageElement;
  try {
    img = await carica(file);
  } catch {
    // Un formato che il browser non sa aprire lo giudica il server, come prima.
    return originale();
  }

  const scala = Math.min(1, LATO_MASSIMO / Math.max(img.naturalWidth, img.naturalHeight));
  const larghezza = Math.round(img.naturalWidth * scala);
  const altezza = Math.round(img.naturalHeight * scala);
  const daRidurre = scala < 1;
  const png = file.type === 'image/png';

  if (!daRidurre && !png && file.size <= BYTE_TRASCURABILI) return originale();

  const canvas = document.createElement('canvas');
  canvas.width = larghezza;
  canvas.height = altezza;
  const ctx = canvas.getContext('2d', { willReadFrequently: png })!;
  ctx.drawImage(img, 0, 0, larghezza, altezza);

  const tieniPng = png && haTrasparenza(ctx, larghezza, altezza);
  if (tieniPng && !daRidurre) return originale();

  const tipo = tieniPng ? 'image/png' : 'image/jpeg';
  const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, tipo, QUALITA_JPEG));
  if (!blob || (!daRidurre && blob.size >= file.size)) return originale();

  return {
    dataUrl: await leggi(blob),
    nome: tipo === 'image/jpeg' ? file.name.replace(/\.[^.]+$/, '') + '.jpg' : file.name,
    tipo,
    byte: blob.size,
  };
}

/** Vero se almeno un pixel non e' del tutto opaco. */
function haTrasparenza(ctx: CanvasRenderingContext2D, larghezza: number, altezza: number): boolean {
  const alfa = ctx.getImageData(0, 0, larghezza, altezza).data;
  for (let i = 3; i < alfa.length; i += 4) {
    if (alfa[i] < 255) return true;
  }
  return false;
}

function carica(file: Blob): Promise<HTMLImageElement> {
  return new Promise((ok, ko) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      ok(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      ko(new Error('immagine non leggibile'));
    };
    img.src = url;
  });
}

function leggi(file: Blob): Promise<string> {
  return new Promise((ok, ko) => {
    const reader = new FileReader();
    reader.onload = () => ok(reader.result as string);
    reader.onerror = () => ko(reader.error);
    reader.readAsDataURL(file);
  });
}
